import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { createDeepeningJourney } from '../../utils/friendJourneys';
import {
  fetchJourneyPersonalization,
  hasJourneyReflectionContent
} from '../../utils/journeyPersonalization';
import WeekOne from './weeks/WeekOne';
import WeekTwo from './weeks/WeekTwo';
import WeekThree from './weeks/WeekThree';
import WeekFour from './weeks/WeekFour';
import JourneyOverview from './JourneyOverview';
import { isJourneyPersonalizationEnabled } from '../../config/featureFlags';

const DeepeningRelationships = ({ onBack, friendId = null, journeyId = null }) => {
  const journeyAiEnabled = isJourneyPersonalizationEnabled();
  const [currentView, setCurrentView] = useState('overview'); // 'overview', 'week1', 'week2', 'week3', 'week4'
  const [friend, setFriend] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({
    week1: { completed: 0, total: 6 },
    week2: { completed: 0, total: 4 },
    week3: { completed: 0, total: 4 },
    week4: { completed: 0, total: 3 }
  });
  const [completedActivities, setCompletedActivities] = useState({
    week1: [],
    week2: [],
    week3: [],
    week4: []
  });
  const [reflectionAnswers, setReflectionAnswers] = useState({
    week1: {},
    week2: {},
    week3: {},
    week4: {}
  });
  const [personalization, setPersonalization] = useState(null);
  const [personalizationLoading, setPersonalizationLoading] = useState(false);
  const [personalizationError, setPersonalizationError] = useState(null);
  const { currentUser, userProgress, saveUserProgress, loadUserProgress } = useAuth();
  const { t, language } = useTranslation();

  // Load friend data when friendId is provided
  useEffect(() => {
    const loadFriendData = async () => {
      if (friendId && currentUser) {
        setLoading(true);
        try {
          const friendDoc = await getDoc(doc(db, 'friends', friendId));
          if (friendDoc.exists()) {
            setFriend({ id: friendDoc.id, ...friendDoc.data() });
          }
        } catch (error) {
          console.error('Error loading friend data:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadFriendData();
  }, [friendId, currentUser]);

  const journeyData = {
    title: t('reconnectAndDeepen'),
    description: t('journeyDescription'),
    totalWeeks: 4,
    objectives: [
      t('objectiveReachOut'),
      t('objectiveBuildRhythms'),
      t('objectiveDeepenConnection')
    ]
  };

  // Load user progress when component mounts
  useEffect(() => {
    const loadProgress = async () => {
      if (currentUser) {
        if (friendId && friend) {
          // Load specific journey progress if journeyId is provided
          if (journeyId && friend.journeys) {
            const specificJourney = friend.journeys.find(j => j.id === journeyId);
            if (specificJourney) {
              const loadedCompletedActivities = specificJourney.completedActivities || {
                week1: [],
                week2: [],
                week3: [],
                week4: []
              };
              
              // Recalculate progress based on actual completed activities array lengths
              // Cap completed at total to prevent over-completion display
              const recalculatedProgress = {
                week1: { 
                  completed: Math.min((loadedCompletedActivities.week1 || []).length, 6), 
                  total: 6 
                },
                week2: { 
                  completed: Math.min((loadedCompletedActivities.week2 || []).length, 4), 
                  total: 4 
                },
                week3: { 
                  completed: Math.min((loadedCompletedActivities.week3 || []).length, 4), 
                  total: 4 
                },
                week4: { 
                  completed: Math.min((loadedCompletedActivities.week4 || []).length, 3), 
                  total: 3 
                }
              };
              
              setProgress(recalculatedProgress);
              setCompletedActivities(loadedCompletedActivities);
              setReflectionAnswers(specificJourney.reflectionAnswers || {
                week1: {},
                week2: {},
                week3: {},
                week4: {}
              });
              setPersonalization(specificJourney.personalization ?? null);
              return;
            }
          }
          
          // Load most recent journey progress
          if (friend.journeys && friend.journeys.length > 0) {
            const latestJourney = friend.journeys[friend.journeys.length - 1];
            const loadedCompletedActivities = latestJourney.completedActivities || {
              week1: [],
              week2: [],
              week3: [],
              week4: []
            };
            
            // Recalculate progress based on actual completed activities array lengths
            // Cap completed at total to prevent over-completion display
            const recalculatedProgress = {
              week1: { 
                completed: Math.min((loadedCompletedActivities.week1 || []).length, 6), 
                total: 6 
              },
              week2: { 
                completed: Math.min((loadedCompletedActivities.week2 || []).length, 4), 
                total: 4 
              },
              week3: { 
                completed: Math.min((loadedCompletedActivities.week3 || []).length, 4), 
                total: 4 
              },
              week4: { 
                completed: Math.min((loadedCompletedActivities.week4 || []).length, 3), 
                total: 3 
              }
            };
            
            setProgress(recalculatedProgress);
            setCompletedActivities(loadedCompletedActivities);
            setReflectionAnswers(latestJourney.reflectionAnswers || {
              week1: {},
              week2: {},
              week3: {},
              week4: {}
            });
            setPersonalization(latestJourney.personalization ?? null);
          } else {
            // Default progress for new journey
            setProgress({
              week1: { completed: 0, total: 6 },
              week2: { completed: 0, total: 4 },
              week3: { completed: 0, total: 4 },
              week4: { completed: 0, total: 3 }
            });
            setCompletedActivities({
              week1: [],
              week2: [],
              week3: [],
              week4: []
            });
            setPersonalization(null);
          }
        } else if (!friendId) {
          // Load general user progress
          const userData = await loadUserProgress();
          if (userData) {
            const loadedCompletedActivities = userData.completedActivities || {
              week1: [],
              week2: [],
              week3: [],
              week4: []
            };
            
            // Recalculate progress based on actual completed activities array lengths
            // Cap completed at total to prevent over-completion display
            const recalculatedProgress = {
              week1: { 
                completed: Math.min((loadedCompletedActivities.week1 || []).length, 6), 
                total: 6 
              },
              week2: { 
                completed: Math.min((loadedCompletedActivities.week2 || []).length, 4), 
                total: 4 
              },
              week3: { 
                completed: Math.min((loadedCompletedActivities.week3 || []).length, 4), 
                total: 4 
              },
              week4: { 
                completed: Math.min((loadedCompletedActivities.week4 || []).length, 3), 
                total: 3 
              }
            };
            
            setProgress(recalculatedProgress);
            setCompletedActivities(loadedCompletedActivities);
            setReflectionAnswers(userData.reflectionAnswers || {
              week1: {},
              week2: {},
              week3: {},
              week4: {}
            });
            setPersonalization(userData.journeyPersonalization ?? null);
          }
        }
      }
    };
    
    loadProgress();
  }, [currentUser, loadUserProgress, friendId, friend, journeyId]);

  // Update progress when userProgress changes
  useEffect(() => {
    if (userProgress) {
      setProgress(userProgress);
    }
  }, [userProgress]);

  const handleProgressUpdate = async (week, activityIds) => {
    // Use actual completed count (don't cap here - let user see real progress)
    const weekTotal = progress[week]?.total || (week === 'week1' ? 6 : week === 'week2' ? 4 : week === 'week3' ? 4 : 3);
    const completed = activityIds.length;
    const newProgress = {
      ...progress,
      [week]: { ...progress[week], completed, total: weekTotal }
    };
    
    const newCompletedActivities = {
      ...completedActivities,
      [week]: activityIds
    };
    
    setProgress(newProgress);
    setCompletedActivities(newCompletedActivities);
    
    // Save to Firebase if user is authenticated
    if (currentUser) {
      if (friendId) {
        try {
          const { updateDoc } = await import('firebase/firestore');
          const friendRef = doc(db, 'friends', friendId);
          const friendSnap = await getDoc(friendRef);
          if (!friendSnap.exists()) return;

          const snapData = friendSnap.data();
          let journeys = [...(snapData.journeys || [])];

          const withProgress = (base) => ({
            ...base,
            progress: newProgress,
            completedActivities: newCompletedActivities,
            reflectionAnswers
          });

          if (journeyId) {
            const idx = journeys.findIndex(j => j.id === journeyId);
            if (idx >= 0) {
              journeys[idx] = withProgress(journeys[idx]);
            } else {
              journeys.push(
                withProgress({
                  ...createDeepeningJourney(journeyId),
                  reflectionAnswers: reflectionAnswers || {
                    week1: {},
                    week2: {},
                    week3: {},
                    week4: {}
                  }
                })
              );
            }
          } else if (journeys.length > 0) {
            const lastIdx = journeys.length - 1;
            journeys[lastIdx] = withProgress(journeys[lastIdx]);
          } else {
            const newId = `deepening-relationships-${Date.now()}`;
            journeys.push(
              withProgress({
                ...createDeepeningJourney(newId),
                reflectionAnswers: reflectionAnswers || {
                  week1: {},
                  week2: {},
                  week3: {},
                  week4: {}
                }
              })
            );
          }

          await updateDoc(friendRef, { journeys });
          setFriend({ id: friendSnap.id, ...snapData, journeys });
        } catch (error) {
          console.error('Error saving journey progress:', error);
        }
      } else {
        // Save general user progress
        const { setDoc } = await import('firebase/firestore');
        await setDoc(doc(db, 'users', currentUser.uid), {
          progress: newProgress,
          completedActivities: newCompletedActivities,
          reflectionAnswers
        }, { merge: true });
      }
    }
  };

  const handleReflectionUpdate = async (week, answers) => {
    const newReflectionAnswers = {
      ...reflectionAnswers,
      [week]: answers
    };
    
    setReflectionAnswers(newReflectionAnswers);
    
    // Save to Firebase if user is authenticated
    if (currentUser) {
      if (friendId) {
        try {
          const { updateDoc } = await import('firebase/firestore');
          const friendRef = doc(db, 'friends', friendId);
          const friendSnap = await getDoc(friendRef);
          if (!friendSnap.exists()) return;

          const snapData = friendSnap.data();
          let journeys = [...(snapData.journeys || [])];

          const withReflection = (base) => ({
            ...base,
            reflectionAnswers: newReflectionAnswers
          });

          if (journeyId) {
            const idx = journeys.findIndex(j => j.id === journeyId);
            if (idx >= 0) {
              journeys[idx] = withReflection(journeys[idx]);
            } else {
              journeys.push(withReflection(createDeepeningJourney(journeyId)));
            }
          } else if (journeys.length > 0) {
            const lastIdx = journeys.length - 1;
            journeys[lastIdx] = withReflection(journeys[lastIdx]);
          } else {
            const newId = `deepening-relationships-${Date.now()}`;
            journeys.push(withReflection(createDeepeningJourney(newId)));
          }

          await updateDoc(friendRef, { journeys });
          setFriend({ id: friendSnap.id, ...snapData, journeys });
        } catch (error) {
          console.error('Error saving journey reflections:', error);
        }
      } else {
        // Save general user reflection answers
        const { setDoc } = await import('firebase/firestore');
        await setDoc(doc(db, 'users', currentUser.uid), {
          reflectionAnswers: newReflectionAnswers
        }, { merge: true });
      }
    }
  };

  const persistJourneyPersonalization = async (nextPersonalization) => {
    if (!currentUser) return;
    if (friendId) {
      try {
        const { updateDoc } = await import('firebase/firestore');
        const friendRef = doc(db, 'friends', friendId);
        const friendSnap = await getDoc(friendRef);
        if (!friendSnap.exists()) return;

        const snapData = friendSnap.data();
        let journeys = [...(snapData.journeys || [])];

        const patchJourney = (base) => ({
          ...base,
          personalization: nextPersonalization
        });

        if (journeyId) {
          const idx = journeys.findIndex(j => j.id === journeyId);
          if (idx >= 0) {
            journeys[idx] = patchJourney(journeys[idx]);
          } else {
            journeys.push(patchJourney(createDeepeningJourney(journeyId)));
          }
        } else if (journeys.length > 0) {
          const lastIdx = journeys.length - 1;
          journeys[lastIdx] = patchJourney(journeys[lastIdx]);
        } else {
          const newId = `deepening-relationships-${Date.now()}`;
          journeys.push(patchJourney(createDeepeningJourney(newId)));
        }

        await updateDoc(friendRef, { journeys });
        setFriend({ id: friendSnap.id, ...snapData, journeys });
      } catch (error) {
        console.error('Error saving journey personalization:', error);
      }
    } else {
      try {
        const { setDoc } = await import('firebase/firestore');
        await setDoc(doc(db, 'users', currentUser.uid), {
          journeyPersonalization: nextPersonalization
        }, { merge: true });
      } catch (error) {
        console.error('Error saving journey personalization:', error);
      }
    }
  };

  const handleRefreshPersonalization = async () => {
    if (!journeyAiEnabled || !currentUser || !hasJourneyReflectionContent(reflectionAnswers)) return;
    setPersonalizationLoading(true);
    setPersonalizationError(null);
    try {
      const data = await fetchJourneyPersonalization({
        reflectionAnswers,
        locale: language,
        friendName: friend?.name || null
      });
      const next = data?.personalization;
      if (!next) {
        throw new Error(t('personalizationEmptyError'));
      }
      setPersonalization(next);
      await persistJourneyPersonalization(next);
    } catch (error) {
      console.error('Personalization error:', error);
      const msg = error?.message || t('personalizationGenericError');
      setPersonalizationError(msg);
    } finally {
      setPersonalizationLoading(false);
    }
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'overview':
        return (
          <JourneyOverview 
            journeyData={journeyData}
            progress={progress}
            onStartWeek={(week) => setCurrentView(week)}
            friend={friend}
            loading={loading}
            onBack={onBack}
            personalization={journeyAiEnabled ? personalization : null}
            onRefreshPersonalization={
              journeyAiEnabled && currentUser ? handleRefreshPersonalization : undefined
            }
            personalizationLoading={journeyAiEnabled && personalizationLoading}
            personalizationError={journeyAiEnabled ? personalizationError : null}
            hasReflectionsForPersonalization={
              journeyAiEnabled && hasJourneyReflectionContent(reflectionAnswers)
            }
          />
        );
      case 'week1':
        return (
          <WeekOne 
            onBack={() => setCurrentView('overview')}
            onNext={() => setCurrentView('week2')}
            onProgressUpdate={(activityIds) => handleProgressUpdate('week1', activityIds)}
            onReflectionUpdate={(answers) => handleReflectionUpdate('week1', answers)}
            initialCompletedActivities={completedActivities.week1}
            initialReflectionAnswers={reflectionAnswers.week1}
            friend={friend}
          />
        );
      case 'week2':
        return (
          <WeekTwo 
            onBack={() => setCurrentView('overview')}
            onNext={() => setCurrentView('week3')}
            onProgressUpdate={(activityIds) => handleProgressUpdate('week2', activityIds)}
            onReflectionUpdate={(answers) => handleReflectionUpdate('week2', answers)}
            initialCompletedActivities={completedActivities.week2}
            initialReflectionAnswers={reflectionAnswers.week2}
            friend={friend}
          />
        );
      case 'week3':
        return (
          <WeekThree 
            onBack={() => setCurrentView('overview')}
            onNext={() => setCurrentView('week4')}
            onProgressUpdate={(activityIds) => handleProgressUpdate('week3', activityIds)}
            onReflectionUpdate={(answers) => handleReflectionUpdate('week3', answers)}
            initialCompletedActivities={completedActivities.week3}
            initialReflectionAnswers={reflectionAnswers.week3}
            friend={friend}
          />
        );
      case 'week4':
        return (
          <WeekFour 
            onBack={() => setCurrentView('overview')}
            onComplete={() => setCurrentView('overview')}
            onProgressUpdate={(activityIds) => handleProgressUpdate('week4', activityIds)}
            onReflectionUpdate={(answers) => handleReflectionUpdate('week4', answers)}
            initialCompletedActivities={completedActivities.week4}
            initialReflectionAnswers={reflectionAnswers.week4}
            friend={friend}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="journey-container">
      <div className="journey-content">
        {renderCurrentView()}
      </div>
    </div>
  );
};

export default DeepeningRelationships;